import { FinancialInputs, ProjectionEvent, PropertyModelEvent, PropertyRiskProfile } from '../types';

const structuralEventMonth = (condition: string) => {
  if (condition === 'severe') return 3;
  if (condition === 'moderate') return 9;
  if (condition === 'monitor') return 18;
  return null;
};

const resolvePercentEventBase = (event: PropertyModelEvent, inputs: FinancialInputs): number => {
  if (event.driver === 'tax_increase') return Number(inputs.annualTaxes || 0);
  if (event.driver === 'insurance') return Number(inputs.annualInsurance || 0);
  if (event.driver === 'repair') return Number(inputs.annualMaintenance || 0);
  if (event.driver === 'rent_change') return Number(inputs.annualRent || 0);
  if (event.driver === 'vacancy_change') return Number(inputs.annualRent || 0);
  if (event.category === 'income') return Number(inputs.annualRent || 0);
  return Number(inputs.annualTaxes || 0)
    + Number(inputs.annualInsurance || 0)
    + Number(inputs.annualMaintenance || 0)
    + Number(inputs.annualHoa || 0)
    + (Number(inputs.utilitiesMonthly || 0) * 12);
};

const resolvePropertyEventAmount = (event: PropertyModelEvent, inputs: FinancialInputs): number => {
  if (event.valueType === 'percent') {
    return resolvePercentEventBase(event, inputs) * ((Number(event.percent || 0)) / 100);
  }
  return Number(event.amount || 0);
};

const convertPropertyEventToProjectionEvents = (event: PropertyModelEvent, horizonMonths: number, inputs: FinancialInputs): ProjectionEvent[] => {
  if (event.status !== 'active') return [];
  const startYear = event.type === 'recurring' ? Math.max(1, Number(event.startYear || event.year || 1)) : Math.max(1, Number(event.year || event.startYear || 1));
  const endYear = event.type === 'recurring' ? Math.max(startYear, Number(event.endYear || startYear)) : startYear;
  const eventYears = event.type === 'recurring'
    ? Array.from({ length: Math.max(1, endYear - startYear + 1) }, (_, index) => startYear + index)
    : [startYear];
  const resolvedAmount = resolvePropertyEventAmount(event, inputs);

  return eventYears.flatMap((year) => {
    if (event.type === 'recurring' && (event.category === 'income' || event.category === 'expense' || event.category === 'maintenance')) {
      const monthlyAmount = resolvedAmount / 12;
      return Array.from({ length: 12 }, (_, monthIndex) => {
        const month = Math.min(horizonMonths, Math.max(1, ((year - 1) * 12) + monthIndex + 1));
        return {
          id: `${event.id}-${year}-${monthIndex + 1}`,
          month,
          category: event.category,
          amount: monthlyAmount,
          label: event.name,
          impactType: (event.category === 'income' ? 'income' : 'expense') as 'income' | 'expense',
          notes: event.notes
        };
      }).filter((projectionEvent) => projectionEvent.month <= horizonMonths);
    }

    const month = Math.min(horizonMonths, Math.max(1, year * 12));
    return [{
      id: `${event.id}-${year}`,
      month,
      category: event.category,
      amount: resolvedAmount,
      label: event.name,
      impactType: (event.category === 'income' ? 'income' : 'expense') as 'income' | 'expense',
      notes: event.notes
    }];
  });
};

export const applyEventSchedule = (inputs: FinancialInputs, riskProfile: PropertyRiskProfile, horizonMonths: number, propertyEvents: PropertyModelEvent[] = []): ProjectionEvent[] => {
  const derivedEvents: ProjectionEvent[] = [];

  Object.values(riskProfile.majorSystems).forEach((system) => {
    if (system.replacementCost <= 0) return;
    const dueMonths = Math.max(1, Math.round((system.expectedLife - system.age) * 12));
    if (dueMonths <= horizonMonths && (system.condition === 'watch' || system.condition === 'critical' || system.priority === 'high')) {
      derivedEvents.push({
        id: `major-${system.key}`,
        month: Math.min(horizonMonths, dueMonths),
        category: 'capex',
        amount: system.replacementCost,
        label: `${system.label} replacement`,
        impactType: 'expense',
        notes: system.notes
      });
    }
  });

  Object.values(riskProfile.structural).forEach((item) => {
    const month = structuralEventMonth(item.condition);
    if (month && item.estimatedCost > 0 && month <= horizonMonths) {
      derivedEvents.push({
        id: `structural-${item.key}`,
        month,
        category: 'maintenance',
        amount: item.estimatedCost,
        label: `${item.label} remediation`,
        impactType: 'expense',
        notes: item.notes
      });
    }
  });

  const explicitEvents = propertyEvents.flatMap((event) => convertPropertyEventToProjectionEvents(event, horizonMonths, inputs));
  return [...derivedEvents, ...explicitEvents].sort((a, b) => a.month - b.month);
};
